import { useEffect, useRef, useState } from 'react';
import type { SearchStage } from './search';

/**
 * The facts «Ищем водителя» shows between its own sentences while nobody has taken
 * the ride — `facts.dart` in the phone app, turn for turn. Russian Wikipedia's
 * «Знаете ли вы?», a thousand of them picked by hand from the rubric's archive
 * (`scripts/facts.py`), one a line, in `assets/facts.txt`: a copy of the phone's,
 * which `facts.spec.ts` holds to it. The text is CC BY-SA.
 *
 * In the bundle rather than fetched from the server, but in a chunk of its own: the
 * page does not wait for 140 KB of facts before it can take an order, and a search is
 * on screen for seconds before the first fact is wanted.
 */

/**
 * How long a stage's own sentence stands before the first fact takes its place, and
 * how long a fact and the sentence after it then stand in turn.
 */
export const LEAD_FIRST = 8_000;
export const FACT_FOR = 12_000;
export const LEAD_BETWEEN = 6_000;

/**
 * Which fact the line under the stage is on, `since` milliseconds after the stage
 * began: null while it is on the stage's own sentence, and otherwise how many facts
 * the stage has shown before this one. The sentence comes first and comes back
 * between facts; the last minute shows none, since what it says is the one thing the
 * passenger has to read.
 */
export function factSlot(stage: SearchStage, since: number): number | null {
  if (stage === 'lastMinute' || since < LEAD_FIRST) return null;
  const cycle = FACT_FOR + LEAD_BETWEEN;
  const into = since - LEAD_FIRST;
  return into % cycle < FACT_FOR ? Math.floor(into / cycle) : null;
}

/** A small seeded generator, so a shuffle comes out the same from the same seed. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * The facts in the order this browser shows them, and how far it has got. The order
 * is a shuffle seeded once and kept, so a passenger goes through every fact before
 * the first comes round again; a list of another length — a build that adds or drops
 * facts — is a new shuffle from the start.
 */
export class FactDeck {
  readonly order: number[];
  next: number;

  constructor(
    readonly facts: string[],
    readonly seed: number,
    next = 0,
  ) {
    const random = mulberry32(seed);
    this.order = facts.map((_, i) => i);
    for (let i = this.order.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [this.order[i], this.order[j]] = [this.order[j]!, this.order[i]!];
    }
    this.next = next % facts.length;
  }

  draw(): string {
    const fact = this.facts[this.order[this.next]!]!;
    this.next = (this.next + 1) % this.facts.length;
    return fact;
  }
}

const KEY = 'limuzin.customer.fact-place';

interface Place {
  seed: number;
  next: number;
  count: number;
}

function readPlace(): Place | null {
  try {
    const place: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    if (place && typeof place === 'object') {
      const { seed, next, count } = place as Record<string, unknown>;
      if ([seed, next, count].every((n) => Number.isInteger(n))) {
        return { seed, next, count } as Place;
      }
    }
  } catch {
    // A private window, or something this build does not understand: start again.
  }
  return null;
}

function writePlace(deck: FactDeck): void {
  try {
    localStorage.setItem(
      KEY,
      JSON.stringify({ seed: deck.seed, next: deck.next, count: deck.facts.length }),
    );
  } catch {
    // It holds for this page and no longer: a repeat is all that costs.
  }
}

/** The facts as written, a line each. */
export function parseFacts(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/** This browser's deck, picking up where it left off. */
export function deckFor(facts: string[]): FactDeck {
  const place = readPlace();
  return place && place.count === facts.length
    ? new FactDeck(facts, place.seed, place.next)
    : new FactDeck(facts, Math.floor(Math.random() * 2 ** 31));
}

let loading: Promise<FactDeck | null> | null = null;

/** Read once per page, the first time a search is on screen. Null where it fails. */
function loadDeck(): Promise<FactDeck | null> {
  loading ??= import('../assets/facts.txt?raw')
    .then(({ default: text }) => {
      const facts = parseFacts(text);
      return facts.length > 0 ? deckFor(facts) : null;
    })
    .catch(() => {
      // A chunk that did not arrive: the line keeps to its sentence, and the next
      // search tries again.
      loading = null;
      return null;
    });
  return loading;
}

/**
 * The fact the line under `stage` shows at `now`, or null while it shows the stage's
 * own sentence. A fact is drawn as its turn begins and held until the turn is over;
 * a stage the screen opens on part way through starts its turns from then, sentence
 * first.
 */
export function useFact(stage: SearchStage, now: number): { fact: string; slot: number } | null {
  const [deck, setDeck] = useState<FactDeck | null>(null);
  const [shown, setShown] = useState<{ stage: SearchStage; slot: number; fact: string } | null>(
    null,
  );
  const since = useRef({ stage, at: now });
  if (since.current.stage !== stage) since.current = { stage, at: now };
  const slot = factSlot(stage, now - since.current.at);

  useEffect(() => {
    let live = true;
    void loadDeck().then((loaded) => {
      if (live) setDeck(loaded);
    });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (slot === null || !deck) return;
    const fact = deck.draw();
    writePlace(deck);
    setShown({ stage, slot, fact });
  }, [stage, slot, deck]);

  return slot !== null && shown?.stage === stage && shown.slot === slot ? shown : null;
}
